import "./index.css";
import { useState, useEffect } from "react";
import Papa from "papaparse";

type Section = "inici" | "portfolios" | "xat" | "recerca" | "blog";

// Mapatge de portfolios a fitxers CSV
const portfolioFiles: Record<string, { file: string; subtitle: string }> = {
  "Berkshire Hathaway": { file: "Berkshire.csv", subtitle: "Warren Buffett" },
  "Pershing Square": { file: "Pershing_Square.csv", subtitle: "Bill Ackman" },
  "Scion Asset": { file: "Scion.csv", subtitle: "Michael Burry" },
  "Appaloosa LP": { file: "AppaloosaLP.csv", subtitle: "David Tepper" },
  "Tiger Global": { file: "Tiger.csv", subtitle: "Chase Coleman" },
  "Bridgewater Associates": { file: "Bridgewater.csv", subtitle: "Ray Dalio" },
  "Soros Fund": { file: "Soros.csv", subtitle: "George Soros" },
  "Duquesne Family": { file: "Duquesne.csv", subtitle: "Stanley Druckenmiller" },
  "Elliott Investment": { file: "Elliot.csv", subtitle: "Paul Singer" },
  "Trump Administration": { file: "Trump.csv", subtitle: "President" },
  "Nancy Pelosi": { file: "Nancy.csv", subtitle: "Democrat" },
  "Cleo Fields": { file: "Cleo.csv", subtitle: "Democrat" },
  "Josh Gottheimer": { file: "Josh.csv", subtitle: "Democrat" },
  "Ro Khanna": { file: "Ro.csv", subtitle: "Democrat" },
  "Marjorie Taylor Greene": { file: "Marjorie.csv", subtitle: "Republican" },
  "Tommy Tuberville": { file: "Tommy.csv", subtitle: "Republican" },
  "Markwayne Mullin": { file: "Markwayne.csv", subtitle: "Republican" },
  "Michael McCaul": { file: "Michael.csv", subtitle: "Republican" },
  "Dave McCormick": { file: "Dave.csv", subtitle: "Republican" },
  "Rare Earths": { file: "RareEarths.csv", subtitle: "Materials crítics" },
  "Commodities": { file: "Commodities.csv", subtitle: "Matèries primeres" },
  "Nuclear": { file: "Nuclear.csv", subtitle: "Energia nuclear" },
  "Space Technology": { file: "Space.csv", subtitle: "Tecnologia espacial" },
  "Inflation": { file: "Inflation.csv", subtitle: "Protecció inflació" },
  "Drones": { file: "Drones.csv", subtitle: "Vehicles aeris" },
  "Batteries": { file: "Batteries.csv", subtitle: "Emmagatzematge" },
  "Renewable Energy": { file: "Renewable.csv", subtitle: "Energia verda" },
  "Biotechnology": { file: "Biotechnology.csv", subtitle: "Biotech" },
  "Stability": { file: "Stability.csv", subtitle: "Empreses estables" },
};

export const App = () => {
  const [activeSection, setActiveSection] = useState<Section>("inici");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const path = window.location.pathname.slice(1) as Section;
    if (path && ["inici", "portfolios", "xat", "recerca", "blog"].includes(path)) {
      setActiveSection(path || "inici");
    }
  }, []);

  const handleNavClick = (section: Section) => {
    setActiveSection(section);
    setIsMobileMenuOpen(false);
    window.history.pushState({}, "", `/${section === "inici" ? "" : section}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleKeyDown = (event: React.KeyboardEvent, section: Section) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleNavClick(section);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.slice(1) as Section;
      setActiveSection(path || "inici");
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isMobileMenuOpen]);

  return (
    <div className="flex min-h-screen bg-white">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-50">
        <a 
          href="/"
          onClick={(e) => {
            e.preventDefault();
            handleNavClick("inici");
          }}
          className="flex items-center gap-2"
          aria-label="Anar a Inici"
        >
          <div className="w-3 h-3 rounded-full bg-[#9DD566]" aria-hidden="true" />
          <h1 className="text-xl font-medium text-gray-900">2147</h1>
        </a>
        
        <button
          type="button"
          onClick={toggleMobileMenu}
          className="p-2 text-gray-700 hover:text-gray-900 transition-colors"
          aria-label={isMobileMenuOpen ? "Tancar menú" : "Obrir menú"}
          aria-expanded={isMobileMenuOpen}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {isMobileMenuOpen ? (
              <path d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40 animate-fade-in"
          onClick={toggleMobileMenu}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu */}
      <aside
        className={`lg:hidden fixed top-[73px] left-0 right-0 bottom-0 bg-white z-40 p-8 transition-transform duration-300 ease-[cubic-bezier(0.645,0.045,0.355,1)] ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <nav className="flex flex-col gap-6" role="navigation" aria-label="Navegació principal">
          <a
            href="/"
            className={`text-left text-2xl transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] ${
              activeSection === "inici" ? "text-gray-900 font-medium" : "text-gray-700"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("inici");
            }}
            aria-label="Anar a Inici"
            aria-current={activeSection === "inici" ? "page" : undefined}
          >
            Inici
          </a>
          <a
            href="/portfolios"
            className={`text-left text-2xl transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] flex items-center gap-3 ${
              activeSection === "portfolios" ? "text-gray-900 font-medium" : "text-gray-700"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("portfolios");
            }}
            aria-label="Anar a Portfolios"
            aria-current={activeSection === "portfolios" ? "page" : undefined}
          >
            {activeSection === "portfolios" && (
              <div className="w-3 h-3 rounded-full bg-[#9DD566]" aria-hidden="true" />
            )}
            Portfolios
          </a>
          <a
            href="/xat"
            className={`text-left text-2xl transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] flex items-center gap-3 ${
              activeSection === "xat" ? "text-gray-900 font-medium" : "text-gray-700"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("xat");
            }}
            aria-label="Anar a Assistent"
            aria-current={activeSection === "xat" ? "page" : undefined}
          >
            {activeSection === "xat" && (
              <div className="w-3 h-3 rounded-full bg-[#9DD566]" aria-hidden="true" />
            )}
            Assistent
          </a>
          <a
            href="/recerca"
            className={`text-left text-2xl transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] flex items-center gap-3 ${
              activeSection === "recerca" ? "text-gray-900 font-medium" : "text-gray-700"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("recerca");
            }}
            aria-label="Anar a Recerca"
            aria-current={activeSection === "recerca" ? "page" : undefined}
          >
            {activeSection === "recerca" && (
              <div className="w-3 h-3 rounded-full bg-[#9DD566]" aria-hidden="true" />
            )}
            Recerca
          </a>
          <a
            href="/blog"
            className={`text-left text-2xl transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] flex items-center gap-3 ${
              activeSection === "blog" ? "text-gray-900 font-medium" : "text-gray-700"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("blog");
            }}
            aria-label="Anar a Blog"
            aria-current={activeSection === "blog" ? "page" : undefined}
          >
            {activeSection === "blog" && (
              <div className="w-3 h-3 rounded-full bg-[#9DD566]" aria-hidden="true" />
            )}
            Blog
          </a>
        </nav>
      </aside>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 p-8 flex-col gap-8 border-r border-gray-100 animate-fade-in-left fixed left-0 top-0 h-screen">
        {/* Logo/Title */}
        <a 
          href="/"
          onClick={(e) => {
            e.preventDefault();
            handleNavClick("inici");
          }}
          className="flex items-center gap-2 relative cursor-pointer group"
          aria-label="Anar a Inici"
        >
          <div 
            className={`w-3 h-3 rounded-full bg-[#9DD566] transition-all duration-300 ease-[cubic-bezier(0.645,0.045,0.355,1)] will-change-transform ${
              activeSection === "inici" ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden="true"
          />
          <h1 className={`text-xl font-medium text-gray-900 transition-all duration-300 ease-[cubic-bezier(0.645,0.045,0.355,1)] group-hover:text-gray-600 ${
            activeSection === "inici" ? "" : "-ml-5"
          }`}>
            2147
          </h1>
        </a>

        {/* Navigation */}
        <nav className="flex flex-col gap-4 relative" role="navigation" aria-label="Navegació principal">
          <a
            href="/"
            className={`text-left text-sm transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] will-change-transform hover:translate-x-1 ${
              activeSection === "inici" ? "text-gray-900 font-medium" : "text-gray-700 hover:text-gray-900"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("inici");
            }}
            onKeyDown={(e) => handleKeyDown(e, "inici")}
            tabIndex={0}
            aria-label="Anar a Inici"
            aria-current={activeSection === "inici" ? "page" : undefined}
          >
            Inici
          </a>
          <a
            href="/portfolios"
            className={`text-left text-sm transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] will-change-transform hover:translate-x-1 flex items-center gap-2 ${
              activeSection === "portfolios" ? "text-gray-900 font-medium" : "text-gray-700 hover:text-gray-900"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("portfolios");
            }}
            onKeyDown={(e) => handleKeyDown(e, "portfolios")}
            tabIndex={0}
            aria-label="Anar a Portfolios"
            aria-current={activeSection === "portfolios" ? "page" : undefined}
          >
            <div 
              className={`w-3 h-3 rounded-full bg-[#9DD566] transition-all duration-300 ease-[cubic-bezier(0.645,0.045,0.355,1)] will-change-transform ${
                activeSection === "portfolios" ? "opacity-100 scale-100" : "opacity-0 scale-0"
              }`}
              aria-hidden="true"
            />
            <span className={`transition-all duration-300 ease-[cubic-bezier(0.645,0.045,0.355,1)] ${
              activeSection === "portfolios" ? "" : "-ml-5"
            }`}>
              Portfolios
            </span>
          </a>
          <a
            href="/xat"
            className={`text-left text-sm transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] will-change-transform hover:translate-x-1 flex items-center gap-2 ${
              activeSection === "xat" ? "text-gray-900 font-medium" : "text-gray-700 hover:text-gray-900"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("xat");
            }}
            onKeyDown={(e) => handleKeyDown(e, "xat")}
            tabIndex={0}
            aria-label="Anar a Assistent"
            aria-current={activeSection === "xat" ? "page" : undefined}
          >
            <div 
              className={`w-3 h-3 rounded-full bg-[#9DD566] transition-all duration-300 ease-[cubic-bezier(0.645,0.045,0.355,1)] will-change-transform ${
                activeSection === "xat" ? "opacity-100 scale-100" : "opacity-0 scale-0"
              }`}
              aria-hidden="true"
            />
            <span className={`transition-all duration-300 ease-[cubic-bezier(0.645,0.045,0.355,1)] ${
              activeSection === "xat" ? "" : "-ml-5"
            }`}>
              Assistent
            </span>
          </a>
          <a
            href="/recerca"
            className={`text-left text-sm transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] will-change-transform hover:translate-x-1 flex items-center gap-2 ${
              activeSection === "recerca" ? "text-gray-900 font-medium" : "text-gray-700 hover:text-gray-900"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("recerca");
            }}
            onKeyDown={(e) => handleKeyDown(e, "recerca")}
            tabIndex={0}
            aria-label="Anar a Recerca"
            aria-current={activeSection === "recerca" ? "page" : undefined}
          >
            <div 
              className={`w-3 h-3 rounded-full bg-[#9DD566] transition-all duration-300 ease-[cubic-bezier(0.645,0.045,0.355,1)] will-change-transform ${
                activeSection === "recerca" ? "opacity-100 scale-100" : "opacity-0 scale-0"
              }`}
              aria-hidden="true"
            />
            <span className={`transition-all duration-300 ease-[cubic-bezier(0.645,0.045,0.355,1)] ${
              activeSection === "recerca" ? "" : "-ml-5"
            }`}>
              Recerca
            </span>
          </a>
          <a
            href="/blog"
            className={`text-left text-sm transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] will-change-transform hover:translate-x-1 flex items-center gap-2 ${
              activeSection === "blog" ? "text-gray-900 font-medium" : "text-gray-700 hover:text-gray-900"
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("blog");
            }}
            onKeyDown={(e) => handleKeyDown(e, "blog")}
            tabIndex={0}
            aria-label="Anar a Blog"
            aria-current={activeSection === "blog" ? "page" : undefined}
          >
            <div 
              className={`w-3 h-3 rounded-full bg-[#9DD566] transition-all duration-300 ease-[cubic-bezier(0.645,0.045,0.355,1)] will-change-transform ${
                activeSection === "blog" ? "opacity-100 scale-100" : "opacity-0 scale-0"
              }`}
              aria-hidden="true"
            />
            <span className={`transition-all duration-300 ease-[cubic-bezier(0.645,0.045,0.355,1)] ${
              activeSection === "blog" ? "" : "-ml-5"
            }`}>
              Blog
            </span>
          </a>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col lg:ml-64 pt-[73px] lg:pt-0">
        {activeSection === "inici" && <HomeSection />}
        {activeSection === "portfolios" && <PortfoliosSection />}
        {activeSection === "xat" && <ChatSection />}
        {activeSection === "recerca" && <ResearchSection />}
        {activeSection === "blog" && <BlogSection />}
      </main>
    </div>
  );
};

interface Trade {
  date: string;
  ticker: string;
  company: string;
  type: string;
  shares: string;
  value: string;
  change: string;
  return: string;
}

interface Holding {
  ticker: string;
  company: string;
  price: string;
  shares: string;
  allocation: string;
  change: string;
  d7?: string;
  d30?: string;
  analysts: string;
  mcap?: string;
  pe: string;
}

const PortfolioDetail = ({ name, onBack }: { name: string; onBack: () => void }) => {
  const [activeView, setActiveView] = useState<"trades" | "holdings">("trades");
  const [trades, setTrades] = useState<Trade[]>([]);
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPolitician, setIsPolitician] = useState(false);

  const portfolioInfo = portfolioFiles[name];
  const subtitle = portfolioInfo?.subtitle || "";

  useEffect(() => {
    const loadCSV = async () => {
      setLoading(true);
      const fileName = portfolioInfo?.file;
      
      if (!fileName) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`/data/${fileName}`);
        const csvText = await response.text();
        
        // Separar per ---
        const parts = csvText.split("---");
        
        // Parse Trades
        if (parts[0]) {
          Papa.parse(parts[0].trim(), {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              const firstRow: any = results.data[0] || {};
              const hasPoliticianFormat = "Amount" in firstRow;
              setIsPolitician(hasPoliticianFormat);
              
              const tradesData = results.data.map((row: any) => ({
                date: row.Date || "",
                ticker: (row.Ticker || "").split(" ")[0],
                company: row.Company || "",
                type: row.Type || "",
                shares: row.Shares || row.Amount || "",
                value: row.Value || "",
                change: row.Change || "",
                return: row.Return || "",
              })).filter((t: Trade) => t.ticker && t.date);
              
              setTrades(tradesData);
            },
          });
        }
        
        // Parse Holdings
        if (parts[1]) {
          Papa.parse(parts[1].trim(), {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              const holdingsData = results.data.map((row: any) => ({
                ticker: (row.Ticker || "").split(" ")[0],
                company: row.Company || "",
                price: row.Price || "",
                shares: row.Shares || row["Dollar Amount"] || "",
                allocation: row.Allocation || "",
                change: row.Change || "",
                d7: row["7D"] || "",
                d30: row["30D"] || "",
                analysts: row.Analysts || "",
                mcap: row.Mcap || "",
                pe: row["P/E"] || "",
              })).filter((h: Holding) => h.ticker && h.company);
              
              setHoldings(holdingsData);
            },
          });
        }
        
      } catch (error) {
        console.error("Error loading CSV:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadCSV();
  }, [name, portfolioInfo]);

  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-16 flex-1 animate-fade-in-up">
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">Carregant...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-16 flex-1 animate-fade-in-up">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Tornar
      </button>

      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-medium text-gray-900 mb-2">{name}</h2>
        {subtitle && <p className="text-gray-600">{subtitle}</p>}
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveView("trades")}
          className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 ${
            activeView === "trades"
              ? "border-[#9DD566] text-gray-900"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Trades ({trades.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveView("holdings")}
          className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 ${
            activeView === "holdings"
              ? "border-[#9DD566] text-gray-900"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Holdings ({holdings.length})
        </button>
      </div>

      {/* Trades Table */}
      {activeView === "trades" && (
        <div className="overflow-x-auto -mx-6 lg:mx-0 bg-white rounded-lg border border-gray-200">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Data</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Ticker</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Empresa</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Tipus</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600 whitespace-nowrap">
                    {isPolitician ? "Import" : "Accions"}
                  </th>
                  {!isPolitician && <th className="text-right py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Valor</th>}
                  {!isPolitician && <th className="text-right py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Canvi</th>}
                  <th className="text-right py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Return</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((trade, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{trade.date}</td>
                    <td className="py-3 px-4 font-mono font-medium text-gray-900 whitespace-nowrap">{trade.ticker}</td>
                    <td className="py-3 px-4 text-gray-900 whitespace-nowrap">{trade.company}</td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        trade.type === "New" || trade.type === "Buy" ? "bg-green-100 text-green-800" :
                        trade.type === "Added" ? "bg-blue-100 text-blue-800" :
                        trade.type === "Exit" || trade.type === "Sell" || trade.type === "Sell Partial" ? "bg-red-100 text-red-800" :
                        "bg-gray-100 text-gray-800"
                      }`}>
                        {trade.type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right text-gray-900 whitespace-nowrap">{trade.shares}</td>
                    {!isPolitician && <td className="py-3 px-4 text-right text-gray-900 whitespace-nowrap">{trade.value}</td>}
                    {!isPolitician && <td className="py-3 px-4 text-right text-gray-600 whitespace-nowrap">{trade.change}</td>}
                    <td className={`py-3 px-4 text-right font-medium whitespace-nowrap ${
                      trade.return.includes("-") ? "text-red-600" : "text-green-600"
                    }`}>{trade.return}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Holdings Table */}
      {activeView === "holdings" && (
        <div className="overflow-x-auto -mx-6 lg:mx-0 bg-white rounded-lg border border-gray-200">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Ticker</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Empresa</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Preu</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600 whitespace-nowrap">
                    {isPolitician ? "Import" : "Accions"}
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Al·locació</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Canvi</th>
                  {holdings[0]?.d7 && <th className="text-right py-3 px-4 font-medium text-gray-600 whitespace-nowrap">7D</th>}
                  {holdings[0]?.d30 && <th className="text-right py-3 px-4 font-medium text-gray-600 whitespace-nowrap">30D</th>}
                  {holdings[0]?.analysts && <th className="text-left py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Analistes</th>}
                  {holdings[0]?.mcap && <th className="text-right py-3 px-4 font-medium text-gray-600 whitespace-nowrap">Mcap</th>}
                  <th className="text-right py-3 px-4 font-medium text-gray-600 whitespace-nowrap">P/E</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((holding, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-mono font-medium text-gray-900 whitespace-nowrap">{holding.ticker}</td>
                    <td className="py-3 px-4 text-gray-900 whitespace-nowrap">{holding.company}</td>
                    <td className="py-3 px-4 text-right text-gray-900 whitespace-nowrap">{holding.price}</td>
                    <td className="py-3 px-4 text-right text-gray-900 whitespace-nowrap">{holding.shares}</td>
                    <td className="py-3 px-4 text-right font-medium text-gray-900 whitespace-nowrap">{holding.allocation}</td>
                    <td className={`py-3 px-4 text-right font-medium whitespace-nowrap ${
                      holding.change.includes("-") ? "text-red-600" : "text-green-600"
                    }`}>{holding.change}</td>
                    {holding.d7 && <td className={`py-3 px-4 text-right whitespace-nowrap ${
                      holding.d7.includes("-") ? "text-red-600" : "text-green-600"
                    }`}>{holding.d7}</td>}
                    {holding.d30 && <td className={`py-3 px-4 text-right whitespace-nowrap ${
                      holding.d30.includes("-") ? "text-red-600" : "text-green-600"
                    }`}>{holding.d30}</td>}
                    {holding.analysts && (
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          holding.analysts === "Buy" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                        }`}>
                          {holding.analysts}
                        </span>
                      </td>
                    )}
                    {holding.mcap && <td className="py-3 px-4 text-right text-gray-600 whitespace-nowrap">{holding.mcap}</td>}
                    <td className="py-3 px-4 text-right text-gray-900 whitespace-nowrap">{holding.pe}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};

const PershingSquareDetail = ({ onBack }: { onBack: () => void }) => {
  const [activeView, setActiveView] = useState<"trades" | "holdings">("trades");

  const trades = [
    { date: "Sep 2025", ticker: "GOOGL", company: "Google", type: "Exit", shares: "4.84M", value: "$1.64B", change: "-519K", return: "68.02%" },
    { date: "Sep 2025", ticker: "BN", company: "Brookfield", type: "Exit", shares: "41.02M", value: "$1.81B", change: "-140K", return: "6.78%" },
    { date: "Sep 2025", ticker: "QSR", company: "Restaurant Brands International", type: "Exit", shares: "22.92M", value: "$1.54B", change: "-85K", return: "6.87%" },
    { date: "Sep 2025", ticker: "UBER", company: "Uber", type: "Exit", shares: "30.27M", value: "$2.35B", change: "-31K", return: "16.10%" },
    { date: "Sep 2025", ticker: "GOOG", company: "Google", type: "Hold", shares: "6.32M", value: "$2.15B", change: "0", return: "67.15%" },
    { date: "Sep 2025", ticker: "AMZN", company: "Amazon", type: "Hold", shares: "5.82M", value: "$1.38B", change: "0", return: "7.05%" },
    { date: "Sep 2025", ticker: "CMG", company: "Chipotle", type: "Hold", shares: "21.54M", value: "$825.16M", change: "0", return: "9.60%" },
    { date: "Sep 2025", ticker: "HTZ", company: "Hertz Global Holdings", type: "Hold", shares: "15.24M", value: "$76.28M", change: "0", return: "11.62%" },
    { date: "Sep 2025", ticker: "HLT", company: "Hilton", type: "Hold", shares: "3.03M", value: "$914.58M", change: "0", return: "9.56%" },
    { date: "Sep 2025", ticker: "HHH", company: "Howard Hughes Holdings", type: "Hold", shares: "18.85M", value: "$1.50B", change: "0", return: "6.90%" },
    { date: "Sep 2025", ticker: "SEG", company: "Seaport Entertainment Group", type: "Hold", shares: "5.02M", value: "$96.53M", change: "0", return: "-" },
  ];

  const holdings = [
    { ticker: "UBER", company: "Uber", price: "$77.53", shares: "30.27M", allocation: "16.43%", change: "-3.70%", analysts: "Buy", pe: "87.8" },
    { ticker: "GOOG", company: "Google", price: "$340.70", shares: "6.32M", allocation: "15.08%", change: "-0.90%", analysts: "Buy", pe: "33.7" },
    { ticker: "BN", company: "Brookfield", price: "$44.07", shares: "41.02M", allocation: "12.66%", change: "-5.43%", analysts: "Buy", pe: "-" },
    { ticker: "GOOGL", company: "Google", price: "$339.59", shares: "4.84M", allocation: "11.52%", change: "-0.82%", analysts: "Buy", pe: "42.3" },
    { ticker: "QSR", company: "Restaurant Brands International", price: "$67.15", shares: "22.92M", allocation: "10.77%", change: "0.42%", analysts: "Buy", pe: "23.6" },
    { ticker: "HHH", company: "Howard Hughes Holdings", price: "$79.69", shares: "18.85M", allocation: "10.52%", change: "-0.10%", analysts: "Buy", pe: "15.1" },
    { ticker: "AMZN", company: "Amazon", price: "$236.49", shares: "5.82M", allocation: "9.64%", change: "-2.20%", analysts: "Buy", pe: "33.8" },
    { ticker: "HLT", company: "Hilton", price: "$301.78", shares: "3.03M", allocation: "6.40%", change: "-0.34%", analysts: "Buy", pe: "69.0" },
    { ticker: "CMG", company: "Chipotle", price: "$38.31", shares: "21.54M", allocation: "5.78%", change: "-0.06%", analysts: "Buy", pe: "33.9" },
    { ticker: "SEG", company: "Seaport Entertainment Group", price: "$19.21", shares: "5.02M", allocation: "0.68%", change: "-0.47%", analysts: "Buy", pe: "-" },
    { ticker: "HTZ", company: "Hertz Global Holdings", price: "$5.00", shares: "15.24M", allocation: "0.53%", change: "1.31%", analysts: "Hold", pe: "-" },
  ];

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-16 flex-1 animate-fade-in-up">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Tornar
      </button>

      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-medium text-gray-900 mb-2">Pershing Square</h2>
        <p className="text-gray-600">Bill Ackman</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveView("trades")}
          className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 ${
            activeView === "trades"
              ? "border-[#9DD566] text-gray-900"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Trades
        </button>
        <button
          type="button"
          onClick={() => setActiveView("holdings")}
          className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 ${
            activeView === "holdings"
              ? "border-[#9DD566] text-gray-900"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Holdings
        </button>
      </div>

      {/* Trades Table */}
      {activeView === "trades" && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-600">Data</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Ticker</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Empresa</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Tipus</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Accions</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Valor</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Canvi</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Return</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((trade, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 text-gray-600">{trade.date}</td>
                  <td className="py-3 px-4 font-mono font-medium text-gray-900">{trade.ticker}</td>
                  <td className="py-3 px-4 text-gray-900">{trade.company}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      trade.type === "New" ? "bg-green-100 text-green-800" :
                      trade.type === "Added" ? "bg-blue-100 text-blue-800" :
                      trade.type === "Exit" ? "bg-red-100 text-red-800" :
                      "bg-gray-100 text-gray-800"
                    }`}>
                      {trade.type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900">{trade.shares}</td>
                  <td className="py-3 px-4 text-right text-gray-900">{trade.value}</td>
                  <td className="py-3 px-4 text-right text-gray-600">{trade.change}</td>
                  <td className="py-3 px-4 text-right text-green-600 font-medium">{trade.return}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Holdings Table */}
      {activeView === "holdings" && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-600">Ticker</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Empresa</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Preu</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Accions</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Al·locació</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Canvi</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Analistes</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">P/E</th>
              </tr>
            </thead>
            <tbody>
              {holdings.map((holding, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-mono font-medium text-gray-900">{holding.ticker}</td>
                  <td className="py-3 px-4 text-gray-900">{holding.company}</td>
                  <td className="py-3 px-4 text-right text-gray-900">{holding.price}</td>
                  <td className="py-3 px-4 text-right text-gray-900">{holding.shares}</td>
                  <td className="py-3 px-4 text-right font-medium text-gray-900">{holding.allocation}</td>
                  <td className={`py-3 px-4 text-right font-medium ${
                    holding.change.startsWith("-") ? "text-red-600" : "text-green-600"
                  }`}>{holding.change}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      holding.analysts === "Buy" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                    }`}>
                      {holding.analysts}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900">{holding.pe}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const BerkshireDetail = ({ onBack }: { onBack: () => void }) => {
  const [activeView, setActiveView] = useState<"trades" | "holdings">("trades");

  const trades = [
    { date: "Sep 2025", ticker: "GOOGL", company: "Google", type: "New", shares: "17.82M", value: "$6.07B", change: "+17.82M", return: "68.02%" },
    { date: "Sep 2025", ticker: "CB", company: "Chubb", type: "Added", shares: "31.33M", value: "$9.81B", change: "+4.30M", return: "14.40%" },
    { date: "Sep 2025", ticker: "DVA", company: "DaVita", type: "Exit", shares: "15.53M", value: "$2.07B", change: "-1.64M", return: "11.85%" },
    { date: "Sep 2025", ticker: "DPZ", company: "Domino's Pizza", type: "Added", shares: "2.98M", value: "$1.19B", change: "+348K", return: "9.54%" },
    { date: "Sep 2025", ticker: "NUE", company: "Nucor", type: "Exit", shares: "6.41M", value: "$1.19B", change: "-206K", return: "22.68%" },
    { date: "Sep 2025", ticker: "LAMR", company: "LAMR", type: "Added", shares: "1.20M", value: "-", change: "+32.60K", return: "2.00%" },
    { date: "Sep 2025", ticker: "LEN", company: "Lennar Class A", type: "Added", shares: "7.05M", value: "$795.55M", change: "+1.96K", return: "18.47%" },
    { date: "Sep 2025", ticker: "LEN.B", company: "LEN.B", type: "Added", shares: "180.98K", value: "-", change: "+50", return: "9.00%" },
    { date: "Sep 2025", ticker: "ALLY", company: "ALLY", type: "Hold", shares: "1.27M", value: "-", change: "0", return: "4.24%" },
    { date: "Sep 2025", ticker: "AMZN", company: "Amazon", type: "Hold", shares: "2.28M", value: "$541.19M", change: "0", return: "7.05%" },
    { date: "Sep 2025", ticker: "AXP", company: "American Express", type: "Hold", shares: "1.40M", value: "-", change: "0", return: "9.91%" },
    { date: "Sep 2025", ticker: "AAPL", company: "Apple", type: "Hold", shares: "3.78M", value: "$1.02B", change: "0", return: "17.17%" },
    { date: "Sep 2025", ticker: "BATRK", company: "BATRK", type: "Hold", shares: "3.63K", value: "-", change: "0", return: "10.27%" },
    { date: "Sep 2025", ticker: "BAC", company: "Bank of America", type: "Hold", shares: "9.80M", value: "$528.17M", change: "0", return: "7.34%" },
    { date: "Sep 2025", ticker: "COF", company: "Capital One", type: "Hold", shares: "2.14M", value: "$470.91M", change: "0", return: "0.36%" },
    { date: "Sep 2025", ticker: "CHTR", company: "CHTR", type: "Hold", shares: "1.06M", value: "-", change: "0", return: "19.87%" },
    { date: "Sep 2025", ticker: "CVX", company: "Chevron", type: "Hold", shares: "1.94M", value: "$343.17M", change: "0", return: "10.23%" },
    { date: "Sep 2025", ticker: "KO", company: "Coca-Cola", type: "Hold", shares: "960.00K", value: "-", change: "0", return: "8.92%" },
    { date: "Sep 2025", ticker: "STZ", company: "Constellation Brands", type: "Hold", shares: "13.00M", value: "$2.07B", change: "0", return: "1.66%" },
    { date: "Sep 2025", ticker: "DEO", company: "DEO", type: "Hold", shares: "227.75K", value: "-", change: "0", return: "18.01%" },
    { date: "Sep 2025", ticker: "HEI.A", company: "HEI.A", type: "Hold", shares: "1.29M", value: "-", change: "0", return: "2.15%" },
    { date: "Sep 2025", ticker: "JEF", company: "JEF", type: "Hold", shares: "433.56K", value: "-", change: "0", return: "5.75%" },
    { date: "Sep 2025", ticker: "KHC", company: "Kraft Heinz", type: "Hold", shares: "325.63M", value: "$7.83B", change: "0", return: "15.34%" },
    { date: "Sep 2025", ticker: "KR", company: "Kroger", type: "Hold", shares: "27.26M", value: "$1.75B", change: "0", return: "9.02%" },
    { date: "Sep 2025", ticker: "LLYVK", company: "LLYVK", type: "Hold", shares: "1.44M", value: "-", change: "0", return: "14.67%" },
    { date: "Sep 2025", ticker: "LLYVA", company: "LLYVA", type: "Hold", shares: "456.77K", value: "-", change: "0", return: "14.36%" },
    { date: "Sep 2025", ticker: "FWONK", company: "FWONK", type: "Hold", shares: "125.42K", value: "-", change: "0", return: "14.58%" },
    { date: "Sep 2025", ticker: "LPX", company: "Louisiana-Pacific", type: "Hold", shares: "4.34M", value: "$379.81M", change: "0", return: "12.38%" },
    { date: "Sep 2025", ticker: "MA", company: "Mastercard", type: "Hold", shares: "3.99M", value: "$2.22B", change: "0", return: "6.52%" },
    { date: "Sep 2025", ticker: "MCO", company: "Moody's", type: "Hold", shares: "12.62M", value: "$5.97B", change: "0", return: "0.36%" },
    { date: "Sep 2025", ticker: "NVR", company: "NVR", type: "Hold", shares: "11.11K", value: "-", change: "0", return: "4.29%" },
    { date: "Sep 2025", ticker: "OXY", company: "Occidental Petroleum", type: "Hold", shares: "264.94M", value: "$11.82B", change: "0", return: "5.01%" },
    { date: "Sep 2025", ticker: "POOL", company: "Pool", type: "Hold", shares: "3.07M", value: "$784.82M", change: "0", return: "20.29%" },
    { date: "Sep 2025", ticker: "SIRI", company: "Sirius XM Holdings", type: "Hold", shares: "6.35M", value: "$127.16M", change: "0", return: "11.97%" },
    { date: "Sep 2025", ticker: "UNH", company: "UnitedHealth", type: "Hold", shares: "3.08M", value: "$867.44M", change: "0", return: "4.81%" },
    { date: "Sep 2025", ticker: "VRSN", company: "VeriSign", type: "Hold", shares: "8.02M", value: "$1.96B", change: "0", return: "7.59%" },
    { date: "Sep 2025", ticker: "V", company: "Visa", type: "Hold", shares: "8.30M", value: "$2.77B", change: "0", return: "5.13%" },
    { date: "Sep 2025", ticker: "ALLE", company: "ALLE", type: "Hold", shares: "780.13K", value: "-", change: "0", return: "0.27%" },
    { date: "Sep 2025", ticker: "AON", company: "Aon plc Class A", type: "Hold", shares: "1.75M", value: "$603.41M", change: "0", return: "5.24%" },
    { date: "Sep 2025", ticker: "LILA", company: "Liberty Latin America", type: "Hold", shares: "1.63M", value: "$12.44M", change: "0", return: "3.84%" },
    { date: "Sep 2025", ticker: "LILAK", company: "LILAK", type: "Hold", shares: "1.28M", value: "-", change: "0", return: "5.31%" },
  ];

  const holdings = [
    { ticker: "OXY", company: "Occidental Petroleum", price: "$44.59", shares: "264.94M", allocation: "18.70%", change: "1.89%", analysts: "Buy", pe: "32.2" },
    { ticker: "CB", company: "Chubb", price: "$313.11", shares: "31.33M", allocation: "15.52%", change: "0.84%", analysts: "Buy", pe: "12.8" },
    { ticker: "KHC", company: "Kraft Heinz", price: "$24.04", shares: "325.63M", allocation: "12.39%", change: "2.36%", analysts: "Hold", pe: "-" },
    { ticker: "GOOGL", company: "Google", price: "$340.85", shares: "17.82M", allocation: "9.61%", change: "-1.16%", analysts: "Buy", pe: "42.3" },
    { ticker: "MCO", company: "Moody's", price: "$472.79", shares: "12.62M", allocation: "9.44%", change: "-9.17%", analysts: "Buy", pe: "41.6" },
    { ticker: "V", company: "Visa", price: "$333.79", shares: "8.30M", allocation: "4.38%", change: "0.20%", analysts: "Buy", pe: "-" },
    { ticker: "MA", company: "Mastercard", price: "$556.05", shares: "3.99M", allocation: "3.51%", change: "0.40%", analysts: "Buy", pe: "35.1" },
    { ticker: "STZ", company: "Constellation Brands", price: "$159.39", shares: "13.00M", allocation: "3.28%", change: "2.06%", analysts: "Buy", pe: "-" },
    { ticker: "DVA", company: "DaVita", price: "$133.44", shares: "15.53M", allocation: "3.28%", change: "19.18%", analysts: "Hold", pe: "11.0" },
    { ticker: "VRSN", company: "VeriSign", price: "$244.72", shares: "8.02M", allocation: "3.10%", change: "-2.55%", analysts: "Buy", pe: "-" },
    { ticker: "KR", company: "Kroger", price: "$64.37", shares: "27.26M", allocation: "2.78%", change: "2.16%", analysts: "Buy", pe: "55.0" },
    { ticker: "DPZ", company: "Domino's Pizza", price: "$399.63", shares: "2.98M", allocation: "1.89%", change: "-2.33%", analysts: "Buy", pe: "23.7" },
    { ticker: "NUE", company: "Nucor", price: "$185.52", shares: "6.41M", allocation: "1.88%", change: "2.64%", analysts: "Buy", pe: "21.3" },
    { ticker: "AAPL", company: "Apple", price: "$269.75", shares: "3.78M", allocation: "1.61%", change: "-0.25%", analysts: "Buy", pe: "33.7" },
    { ticker: "UNH", company: "UnitedHealth", price: "$281.21", shares: "3.08M", allocation: "1.37%", change: "-2.19%", analysts: "Buy", pe: "14.9" },
    { ticker: "LEN", company: "Lennar Class A", price: "$112.83", shares: "7.05M", allocation: "1.26%", change: "3.19%", analysts: "Buy", pe: "13.6" },
    { ticker: "POOL", company: "Pool", price: "$255.74", shares: "3.07M", allocation: "1.24%", change: "0.73%", analysts: "Buy", pe: "23.1" },
    { ticker: "AON", company: "Aon plc Class A", price: "$344.38", shares: "1.75M", allocation: "0.95%", change: "-1.23%", analysts: "Hold", pe: "27.9" },
    { ticker: "AMZN", company: "Amazon", price: "$237.78", shares: "2.28M", allocation: "0.86%", change: "-2.60%", analysts: "Buy", pe: "33.8" },
    { ticker: "BAC", company: "Bank of America", price: "$53.90", shares: "9.80M", allocation: "0.84%", change: "0.31%", analysts: "Buy", pe: "14.5" },
    { ticker: "COF", company: "Capital One", price: "$220.36", shares: "2.14M", allocation: "0.75%", change: "-0.99%", analysts: "Buy", pe: "77.4" },
    { ticker: "LPX", company: "Louisiana-Pacific", price: "$87.42", shares: "4.34M", allocation: "0.60%", change: "3.21%", analysts: "Buy", pe: "27.5" },
    { ticker: "CVX", company: "Chevron", price: "$177.08", shares: "1.94M", allocation: "0.54%", change: "1.80%", analysts: "Buy", pe: "24.5" },
    { ticker: "SIRI", company: "Sirius XM Holdings", price: "$20.04", shares: "6.35M", allocation: "0.20%", change: "-1.62%", analysts: "Buy", pe: "6.9" },
    { ticker: "LILA", company: "Liberty Latin America", price: "$7.66", shares: "1.63M", allocation: "0.02%", change: "-1.03%", analysts: "Buy", pe: "-" },
  ];

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-16 flex-1 animate-fade-in-up">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Tornar
      </button>

      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-medium text-gray-900 mb-2">Berkshire Hathaway</h2>
        <p className="text-gray-600">Warren Buffett</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveView("trades")}
          className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 ${
            activeView === "trades"
              ? "border-[#9DD566] text-gray-900"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Trades
        </button>
        <button
          type="button"
          onClick={() => setActiveView("holdings")}
          className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 ${
            activeView === "holdings"
              ? "border-[#9DD566] text-gray-900"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Holdings
        </button>
      </div>

      {/* Trades Table */}
      {activeView === "trades" && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-600">Data</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Ticker</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Empresa</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Tipus</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Accions</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Valor</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Canvi</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Return</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((trade, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 text-gray-600">{trade.date}</td>
                  <td className="py-3 px-4 font-mono font-medium text-gray-900">{trade.ticker}</td>
                  <td className="py-3 px-4 text-gray-900">{trade.company}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      trade.type === "New" ? "bg-green-100 text-green-800" :
                      trade.type === "Added" ? "bg-blue-100 text-blue-800" :
                      "bg-red-100 text-red-800"
                    }`}>
                      {trade.type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900">{trade.shares}</td>
                  <td className="py-3 px-4 text-right text-gray-900">{trade.value}</td>
                  <td className="py-3 px-4 text-right text-gray-600">{trade.change}</td>
                  <td className="py-3 px-4 text-right text-green-600 font-medium">{trade.return}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Holdings Table */}
      {activeView === "holdings" && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-600">Ticker</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Empresa</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Preu</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Accions</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Al·locació</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Canvi</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Analistes</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">P/E</th>
              </tr>
            </thead>
            <tbody>
              {holdings.map((holding, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-mono font-medium text-gray-900">{holding.ticker}</td>
                  <td className="py-3 px-4 text-gray-900">{holding.company}</td>
                  <td className="py-3 px-4 text-right text-gray-900">{holding.price}</td>
                  <td className="py-3 px-4 text-right text-gray-900">{holding.shares}</td>
                  <td className="py-3 px-4 text-right font-medium text-gray-900">{holding.allocation}</td>
                  <td className={`py-3 px-4 text-right font-medium ${
                    holding.change.startsWith("-") ? "text-red-600" : "text-green-600"
                  }`}>{holding.change}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      holding.analysts === "Buy" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                    }`}>
                      {holding.analysts}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900">{holding.pe}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </section>
  );
};

const HomeSection = () => {
  return (
    <section className="max-w-4xl mx-auto px-6 lg:px-12 py-8 lg:py-16 flex-1 animate-fade-in-up">
      <div className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Imagina que poguessis veure exactament com pensen els millors inversors del món. No el que diuen a les entrevistes, sinó el que fan de veritat. I que tinguessis les mateixes eines que ells per analitzar empreses, entendre el mercat i prendre les teves pròpies decisions.
        </p>
        
        <p className="text-gray-900 font-medium text-xl mb-6">
          Això és 2147.
        </p>

        <p className="text-gray-700 leading-relaxed mb-6">
          La majoria de gent inverteix a cegues. Llegeix titulars, escolta opinions, segueix el renou. Però els grans inversors no van així. Ells analitzen dades, estudien empreses, busquen patrons. I sobretot, tenen criteri propi.
        </p>

        <p className="text-gray-700 leading-relaxed mb-6">
          El problema? Que aquesta informació existeix, però està dispersa per tot arreu. Els arxius 13F perduts en PDFs de la SEC. Les dades financeres repartides entre centenars de webs. Els screeners o costen una fortuna o són un infern d'usar. I si vols entendre alguna cosa, toca buscar durant hores.
        </p>

        <p className="text-gray-900 font-medium text-xl mb-6">
          2147 ho reuneix tot.
        </p>

        <p className="text-gray-700 leading-relaxed mb-6">
          Aquí tens els portfolios dels millors inversors. Actualitzats cada trimestre. Pots veure què compra Buffett, què ven Burry, on posa els diners Ray Dalio. I no només veure-ho: comparar-los, analitzar-los, aprendre'n.
        </p>

        <p className="text-gray-700 leading-relaxed mb-6">
          Però també tens les eines per fer recerca pel teu compte. Un screener per filtrar milers d'empreses segons els criteris que tu triïs. Una IA que respon amb dades reals, no amb frases genèriques de manual. I un espai per guardar les teves notes, documentar el que aprens i anar construint el teu criteri.
        </p>

        <p className="text-gray-700 leading-relaxed mb-2">
          Perquè està bé seguir els millors.
        </p>
        
        <p className="text-gray-900 font-medium leading-relaxed mb-6">
          Però el que realment importa és aprendre a pensar com ells.
        </p>

        <p className="text-gray-700 leading-relaxed mb-6">
          2147 no et dirà què comprar demà. No és una app de trading. És una eina per entendre. Per deixar de dependre d'opinions alienes i començar a veure les dades. Per passar de ser algú que reacciona al mercat a algú que sap per què fa el que fa.
        </p>

        <p className="text-gray-700 leading-relaxed mb-2">
          La informació ja hi és.
        </p>
        
        <p className="text-gray-900 font-medium text-lg">
          Només cal saber on mirar.
        </p>
      </div>
    </section>
  );
};

const PortfoliosSection = () => {
  const [activeTab, setActiveTab] = useState<"funds" | "politicians" | "themes">("funds");
  const [selectedPortfolio, setSelectedPortfolio] = useState<string | null>(null);

  const funds = [
    { name: "Berkshire Hathaway", subtitle: "Warren Buffett" },
    { name: "Pershing Square", subtitle: "Bill Ackman" },
    { name: "Scion Asset", subtitle: "Michael Burry" },
    { name: "Appaloosa LP", subtitle: "David Tepper" },
    { name: "Tiger Global", subtitle: "Chase Coleman" },
    { name: "Bridgewater Associates", subtitle: "Ray Dalio" },
    { name: "Soros Fund", subtitle: "George Soros" },
    { name: "Duquesne Family", subtitle: "Stanley Druckenmiller" },
    { name: "Elliott Investment", subtitle: "Paul Singer" },
  ];

  const politicians = [
    { name: "Trump Administration", subtitle: "President" },
    { name: "Nancy Pelosi", subtitle: "Democrat" },
    { name: "Cleo Fields", subtitle: "Democrat" },
    { name: "Josh Gottheimer", subtitle: "Democrat" },
    { name: "Ro Khanna", subtitle: "Democrat" },
    { name: "Marjorie Taylor Greene", subtitle: "Republican" },
    { name: "Tommy Tuberville", subtitle: "Republican" },
    { name: "Markwayne Mullin", subtitle: "Republican" },
    { name: "Michael McCaul", subtitle: "Republican" },
    { name: "Dave McCormick", subtitle: "Republican" },
  ];

  const themes = [
    { name: "Rare Earths", subtitle: "Materials crítics" },
    { name: "Commodities", subtitle: "Matèries primeres" },
    { name: "Nuclear", subtitle: "Energia nuclear" },
    { name: "Space Technology", subtitle: "Tecnologia espacial" },
    { name: "Inflation", subtitle: "Protecció inflació" },
    { name: "Drones", subtitle: "Vehicles aeris" },
    { name: "Batteries", subtitle: "Emmagatzematge" },
    { name: "Renewable Energy", subtitle: "Energia verda" },
    { name: "Biotechnology", subtitle: "Biotech" },
    { name: "Stability", subtitle: "Empreses estables" },
  ];

  const getCurrentList = () => {
    switch (activeTab) {
      case "funds":
        return funds;
      case "politicians":
        return politicians;
      case "themes":
        return themes;
    }
  };

  const handlePortfolioClick = (name: string) => {
    setSelectedPortfolio(name);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackClick = () => {
    setSelectedPortfolio(null);
  };

  if (selectedPortfolio && portfolioFiles[selectedPortfolio]) {
    return <PortfolioDetail name={selectedPortfolio} onBack={handleBackClick} />;
  }

  return (
    <section className="max-w-6xl mx-auto px-6 lg:px-12 py-8 lg:py-16 flex-1 animate-fade-in-up">
      <div className="prose prose-lg max-w-none mb-8">
        <h2 className="text-3xl font-medium text-gray-900 mb-4">Portfolios</h2>
        
        <p className="text-gray-700 leading-relaxed mb-6">
          Cada trimestre, els grans fons d'inversió han de publicar un formulari 13F a la SEC. 
          Aquí pots veure què tenen, què han comprat i què han venut. També pots seguir els 
          portfolios de polítics americans i explorar portfolios temàtics.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-8 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab("funds")}
          className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 ${
            activeTab === "funds"
              ? "border-[#9DD566] text-gray-900"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Fons d'Inversió
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("politicians")}
          className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 ${
            activeTab === "politicians"
              ? "border-[#9DD566] text-gray-900"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Polítics
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("themes")}
          className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 ${
            activeTab === "themes"
              ? "border-[#9DD566] text-gray-900"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Temes
        </button>
      </div>

      {/* Grid de portfolios */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
        {getCurrentList().map((item, index) => (
          <div
            key={index}
            onClick={() => handlePortfolioClick(item.name)}
            className="bg-white border border-gray-200 rounded-lg p-5 hover:border-gray-300 hover:shadow-md transition-all duration-150 cursor-pointer group"
          >
            <h3 className="font-medium text-gray-900 mb-1 group-hover:text-[#9DD566] transition-colors">
              {item.name}
            </h3>
            <p className="text-sm text-gray-600">{item.subtitle}</p>
          </div>
        ))}
      </div>

      <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-[#9DD566]">
        <p className="text-sm text-gray-700 leading-relaxed">
          <span className="font-medium">Nota:</span> Els 13F surten amb 45 dies de retard. 
          No es tracta de copiar, sinó d'entendre on posen el focus els grans inversors 
          i fer la teva pròpia recerca.
        </p>
      </div>
    </section>
  );
};

const ChatSection = () => {
  const [messages, setMessages] = useState<Array<{ role: "user" | "assistant"; content: string }>>([]);
  const [inputValue, setInputValue] = useState("");

  const prompts = [
    "Què és el ROIC i per què importa més que el ROE?",
    "Quines empreses tenen un moat realment defensable?",
    "Com valuaries una empresa tecnològica sense beneficis?",
    "Explica'm la diferència entre value i growth investing",
  ];

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;
    
    setMessages([...messages, { role: "user", content: inputValue }]);
    setInputValue("");
    
    setTimeout(() => {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "L'assistent IA arriba aviat. Estic entrenant un model especialitzat que entén de ràtios financeres, valoracions i estratègies d'inversió. Mentrestant, pots explorar la resta de seccions." 
      }]);
    }, 500);
  };

  const handlePromptClick = (prompt: string) => {
    setInputValue(prompt);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <section className="max-w-4xl mx-auto px-6 lg:px-12 py-8 lg:py-16 flex-1 flex flex-col animate-fade-in-up">
      <div className="mb-8">
        <h2 className="text-3xl font-medium text-gray-900 mb-4">Assistent</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          Una IA entrenada en finances. Li pots preguntar sobre ràtios, valoracions, 
          empreses, estratègies... Respon amb dades, no amb frases genèriques.
        </p>
        <p className="text-sm text-gray-600">
          Encara estic entrenant el model. Arribarà aviat.
        </p>
      </div>

      {messages.length === 0 && (
        <div className="mb-8">
          <p className="text-sm text-gray-600 mb-4">Exemples del que podràs preguntar:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {prompts.map((prompt, index) => (
              <button
                key={index}
                type="button"
                className="text-left text-sm bg-gray-50 hover:bg-gray-100 p-4 rounded-lg transition-all duration-150 border border-gray-200"
                onClick={() => handlePromptClick(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 bg-gray-50 rounded-lg p-6 mb-6 overflow-y-auto min-h-[300px]">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Prova de fer una pregunta...
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg ${
                  message.role === "user"
                    ? "bg-[#9DD566] text-white ml-auto max-w-[80%]"
                    : "bg-white text-gray-900 mr-auto max-w-[80%]"
                }`}
              >
                <p className="text-sm">{message.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Escriu aquí..."
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9DD566] focus:border-transparent text-sm"
          aria-label="Input del xat"
        />
        <button
          type="button"
          onClick={handleSendMessage}
          className="bg-[#9DD566] text-white px-6 py-3 rounded-lg hover:bg-[#8bc455] active:scale-[0.97] transition-all duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] font-medium text-sm will-change-transform"
          aria-label="Enviar missatge"
        >
          Enviar
        </button>
      </div>
    </section>
  );
};

const ResearchSection = () => {
  return (
    <section className="max-w-6xl mx-auto px-6 lg:px-12 py-8 lg:py-16 flex-1 animate-fade-in-up">
      <div className="prose prose-lg max-w-none mb-12">
        <h2 className="text-3xl font-medium text-gray-900 mb-6">Recerca</h2>
        
        <p className="text-gray-700 leading-relaxed mb-4">
          Hi ha milers d'empreses cotitzades. Tu no les pots mirar totes. 
          Necessites filtrar. Això és un screener.
        </p>

        <p className="text-gray-700 leading-relaxed mb-4">
          La majoria de screeners són dolents. O són cars, o estan mal dissenyats, 
          o tenen dades velles. Aquest serà simple, ràpid i amb les mètriques que 
          realment importen.
        </p>

        <p className="text-sm text-gray-600 mb-8">
          Encara l'estic construint. Tindrà ROIC, FCF Yield, P/E, creixement de vendes, 
          marges, Piotroski Score... Les dades que un inversor seriós vol veure.
        </p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 mb-8">
        <h3 className="font-medium text-gray-900 mb-6">Preview del Screener</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label htmlFor="sector" className="block text-sm font-medium text-gray-700 mb-2">
              Sector
            </label>
            <select 
              id="sector"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9DD566] focus:border-transparent text-sm bg-white"
              disabled
            >
              <option>Tots</option>
            </select>
          </div>

          <div>
            <label htmlFor="marketcap" className="block text-sm font-medium text-gray-700 mb-2">
              Market Cap
            </label>
            <select 
              id="marketcap"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9DD566] focus:border-transparent text-sm bg-white"
              disabled
            >
              <option>Qualsevol</option>
            </select>
          </div>

          <div>
            <label htmlFor="pe" className="block text-sm font-medium text-gray-700 mb-2">
              P/E Ratio
            </label>
            <select 
              id="pe"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9DD566] focus:border-transparent text-sm bg-white"
              disabled
            >
              <option>Qualsevol</option>
            </select>
          </div>
        </div>

        <button
          type="button"
          className="bg-gray-300 text-gray-500 px-6 py-3 rounded-lg font-medium text-sm cursor-not-allowed"
          disabled
        >
          Properament
        </button>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-[#9DD566]">
        <p className="text-sm text-gray-700 leading-relaxed">
          El screener serà gratuït. Sense límits artificials. Sense plans premium que 
          amaguen les millors funcions. Si una dada és important per analitzar una empresa, 
          estarà disponible.
        </p>
      </div>
    </section>
  );
};

const BlogSection = () => {
  const posts = [
    {
      title: "Com llegir un 10-K sense morir en l'intent",
      category: "Guies",
      date: "Properament",
      excerpt: "Els informes anuals tenen centenars de pàgines. Però només n'has de llegir 20. T'explico quines.",
    },
    {
      title: "Per què el P/E no serveix per valorar empreses tech",
      category: "Valoració",
      date: "Properament",
      excerpt: "Tothom mira el P/E. Però per empreses com Amazon o Netflix, és un número inútil. Aquí tens el perquè.",
    },
    {
      title: "Què ha comprat Buffett aquest trimestre",
      category: "13F",
      date: "Properament",
      excerpt: "Berkshire ha publicat el seu últim 13F. Analitzem els canvis i què poden significar.",
    },
    {
      title: "ROIC vs ROE: quin importa més?",
      category: "Mètriques",
      date: "Properament",
      excerpt: "Dues ràtios que semblen iguals però diuen coses molt diferents. Amb exemples reals.",
    },
  ];

  return (
    <section className="max-w-4xl mx-auto px-6 lg:px-12 py-8 lg:py-16 flex-1 animate-fade-in-up">
      <div className="prose prose-lg max-w-none mb-12">
        <h2 className="text-3xl font-medium text-gray-900 mb-6">Blog</h2>
        
        <p className="text-gray-700 leading-relaxed mb-4">
          Notes sobre empreses, valoracions, estratègies. El que vaig aprenent, 
          ho documento aquí.
        </p>

        <p className="text-gray-700 leading-relaxed mb-8">
          Sense clickbait. Sense promeses de "10x el teu portfolio". 
          Només casos reals, números i reflexions honestes.
        </p>
      </div>

      <div className="space-y-6">
        {posts.map((post, index) => (
          <article
            key={index}
            className="bg-gray-50 border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-all duration-150"
          >
            <div className="flex items-start justify-between mb-3">
              <span className="text-xs font-medium text-gray-600">
                {post.category}
              </span>
              <span className="text-xs text-gray-500">{post.date}</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{post.title}</h3>
            <p className="text-sm text-gray-600">{post.excerpt}</p>
          </article>
        ))}
      </div>

      <div className="mt-12 bg-gray-50 p-6 rounded-lg border-l-4 border-[#9DD566]">
        <p className="text-sm text-gray-700 leading-relaxed">
          Publicaré quan tingui alguna cosa que valgui la pena compartir. 
          Sense calendari fix. Sense obligació de fer contingut per fer contingut.
        </p>
      </div>
    </section>
  );
};

export default App;
